import { flow, pipe, tupled, untupled } from "fp-ts/es6/function"

import {
  adjust,
  always,
  both,
  complement,
  cond,
  defaultTo,
  either,
  equals,
  identity,
  ifElse,
  is,
  isEmpty,
  isNil,
  T,
  when as whenR,
} from "ramda"

/**
 * Similar to Ramda.cond, but takes a sequence of
 * `[predicate, transformer]` pairs as arguments, not as a list.
 *
 * Returns a function `fn`, which encapsulates `if/else, if/else, ...` logic.
 * All of the arguments to `fn` are applied to each of the predicates in turn
 * until one returns a truthy value, at which point `fn` returns the result of
 * applying its arguments to the corresponding transformer.
 * If none of the predicates matches, `fn` returns `undefined`.
 */
export const condition = untupled(cond)

export const isNotNil = complement(isNil)

export const notEquals = complement(equals)

/**
 *
 * @param {any} value
 * @param {function|boolean} assertion
 * @param {any} conclusion
 * @param {any} fallback
 * @returns {any|false}
 */
export const consequence = (value, assertion, conclusion = value, fallback = null) =>
  pipe(
    value,
    condition(
      [isNil, always(fallback)],
      [is(Function, assertion) ? assertion : always(Boolean(assertion)), always(conclusion)],
      [T, always(fallback)],
    ),
  )

export const isIdle = either(isNil, isEmpty)

export const isNonEmpty = both(isNotNil, complement(isEmpty))

export const nullable = ifElse(isNotNil, identity, always(null))

/**
 * Tests the arguments by passing them to the given predicate function as a tuple.
 * If the predicate is satisfied, the function will return the result
 * of calling the whenTrueFn function with the arguments tuple.
 * If the predicate is not satisfied, the arguments tuple is returned as is.
 *
 * @param {function} pred A predicate function
 *
 * @param {function} whenTrueFn
 *
 */
export const when = untupled(
  flow(adjust(1, flow(defaultTo(always(undefined)), tupled)), tupled(whenR), untupled),
)
